import { useState } from "react";
import logo from '../../assets/images/logo.png';
import { Eye, EyeOff, LogIn, Loader2, Lock, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from '../../config/apiConfig';
import axios from "../../config/axios";
import { getLastRoute } from '../../utils/sessionRestore';

export default function Login() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setErrors({});

    try {
      const response = await axios.post(`${BASE_URL}/auth/login`, {
        username: formData.username,
        password: formData.password,
      });

      // Backend now returns only user info (token is in HttpOnly cookie)
      const { id, username } = response.data;
      // Only store non-sensitive user info
      localStorage.setItem("userRoleId", id);
      localStorage.setItem("username", username);

      // Optional: save as object
      localStorage.setItem("user", JSON.stringify({ id, username }));

      console.log("Login Success!", { userRoleId: id, username });

      // If this user has a saved last-route from a previous session, send them back there.
      // Otherwise fall back to the dashboard. Falling back also handles fresh logins and
      // any user-no-longer-has-access cases (ProtectedRoute will catch that and bounce).
      const saved = getLastRoute(id);
      const target = saved?.path ? `${saved.path}${saved.search || ''}` : '/dashboard';

      // Force full reload so AuthContext calls /auth/me and detects the cookie.
      window.location.href = target;

    } catch (error) {
      console.error("Login failed:", error);

      let errorMessage = "Invalid username or password. Please try again.";

      if (error.code === 'ERR_NETWORK' || !error.response) {
        errorMessage = "Unable to connect to server. Please check if the server is running and try again.";
      } else if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        // Check if response is HTML (server error page) - not user friendly
        const isHtml = typeof data === 'string' && (data.trim().startsWith('<!') || data.trim().startsWith('<html') || data.includes('<script'));

        if (isHtml) {
          // Server returned an HTML error page - show friendly message based on status
          if (status === 500) errorMessage = "Server error occurred. Please try again later or contact support.";
          else if (status === 502 || status === 503) errorMessage = "Server is temporarily unavailable. Please try again in a few moments.";
          else if (status === 404) errorMessage = "Login service not found. Please contact support.";
          else errorMessage = "Something went wrong on the server. Please try again later.";
        } else if (status === 401 || status === 403) {
          errorMessage = data?.message || "Invalid username or password. Please try again.";
        } else if (status >= 500) {
          errorMessage = "Server error occurred. Please try again later.";
        } else {
          errorMessage = data?.message || data?.title || errorMessage;
        }
      } else if (error.request) {
        errorMessage = "No response from server. Please check your connection.";
      } else {
        errorMessage = "Something went wrong. Please try again.";
      }

      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 flex items-center justify-center p-4">
      <style>{`
        @keyframes fadeIn {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideIn {
          0% {
            opacity: 0;
            transform: translateX(-20px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out;
        }

        .animate-slideIn {
          animation: slideIn 0.6s ease-out;
        }

        .input-focus {
          transition: all 0.2s ease;
        }

        .input-focus:focus {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.15);
        }
      `}</style>

      {/* Login Card */}
      <div className="w-full max-w-md animate-fadeIn">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header with Logo */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-10 text-center">
            <div className="mb-6 flex justify-center">
              <div className="bg-white rounded-xl p-4 shadow-lg">
                <img
                  src={logo}
                  alt="Scanwell Logistics"
                  className="h-16 w-auto"
                  onError={(e) => {
                    // Fallback if logo doesn't load
                    e.target.style.display = "none";
                    e.target.parentElement.innerHTML =
                      '<div class="text-blue-600 font-bold text-2xl">SCANWELL</div>';
                  }}
                />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Welcome Back</h1>
            <p className="text-blue-100">Sign in to your account</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-8 py-8 space-y-6">
            {/* Username Field */}
            <div className="animate-slideIn" style={{ animationDelay: "0.1s", animationFillMode: "both" }}>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Username
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="Enter your username"
                  className={`input-focus w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all duration-200 ${errors.username
                    ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                    : "border-slate-300 focus:ring-blue-500 focus:border-blue-500"
                    }`}
                  disabled={loading}
                />
              </div>
              {errors.username && (
                <p className="mt-1.5 text-sm text-red-600">{errors.username}</p>
              )}
            </div>

            {/* Password Field */}
            <div className="animate-slideIn" style={{ animationDelay: "0.2s", animationFillMode: "both" }}>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  className={`input-focus w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all duration-200 ${errors.password
                    ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                    : "border-slate-300 focus:ring-blue-500 focus:border-blue-500"
                    }`}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors duration-200"
                  tabIndex="-1"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 animate-fadeIn">
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm">{errors.submit}</span>
              </div>
            )}

            {/* Login Button */}
            <div className="animate-slideIn" style={{ animationDelay: "0.3s", animationFillMode: "both" }}>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Signing In...</span>
                  </>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    <span>Sign In</span>
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Footer */}
          <div className="px-8 py-6 bg-slate-50 border-t border-slate-200 text-center animate-fadeIn" style={{ animationDelay: "0.4s", animationFillMode: "both" }}>
            <p className="text-sm text-slate-600">
              © {new Date().getFullYear()} Scanwell Logistics. All rights reserved.
            </p>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-6 text-center animate-fadeIn" style={{ animationDelay: "0.5s", animationFillMode: "both" }}>
          <p className="text-sm text-slate-600">
            Having trouble logging in?{" "}
            <a
              href="mailto:support@scanwell.lk"
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
